// pages/ApnaClinic/BlockedNumbers.jsx
import React, { useState } from "react";
import "./BlockedNumbers.css";
import MainLayout from "../../layouts/MainLayout";
import {Login_Url} from "../Config";
import {Auth_KEY} from "../Config";


const API_BASE = `${Login_Url}api`;
const API_KEY  = `${Auth_KEY}`;

const headers = {
  "Content-Type": "application/json",
  "X-API-Key":    API_KEY,
};

// ── Helpers ───────────────────────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("en-PK", {
    day:    "2-digit",
    month:  "short",
    year:   "numeric",
    hour:   "2-digit",
    minute: "2-digit",
  });
}

function Alert({ type, message, onClose }) {
  if (!message) return null;
  const styles = {
    success: "alert alert-success",
    danger:  "alert alert-danger",
    warning: "alert alert-warning",
    info:    "alert alert-info",
  };
  return (
    <div className={`${styles[type] || "alert alert-info"} alert-dismissible fade show d-flex align-items-center gap-2`} role="alert">
      <i className={`bi ${type === "success" ? "bi-check-circle-fill" : type === "danger" ? "bi-x-circle-fill" : "bi-info-circle-fill"}`}></i>
      <span>{message}</span>
      <button type="button" className="btn-close ms-auto" onClick={onClose}></button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 1 — Check Number
// ─────────────────────────────────────────────────────────────────────────
function CheckNumber() {
  const [phone,   setPhone]   = useState("");
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const [alert,   setAlert]   = useState(null);

  async function handleCheck(e) {
    e.preventDefault();
    if (!phone.trim()) { setAlert({ type: "warning", message: "Phone number required." }); return; }

    setLoading(true);
    setResult(null);
    setAlert(null);

    try {
      const res  = await fetch(`${API_BASE}/blocked/check?phone_number=${encodeURIComponent(phone.trim())}`, { headers });
      const data = await res.json();

      if (!data.success) throw new Error(data.message || "API error");
      setResult(data);
    } catch (err) {
      setAlert({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Alert {...(alert || {})} onClose={() => setAlert(null)} />

      <form onSubmit={handleCheck} className="row g-3 mb-4">
        <div className="col-md-8">
          <label className="form-label fw-semibold">Phone Number</label>
          <div className="input-group">
            <span className="input-group-text bg-light">
              <i className="bi bi-telephone-fill text-primary"></i>
            </span>
            <input
              type="text"
              className="form-control form-control-lg"
              placeholder="e.g. 03001234567"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>
        </div>
        <div className="col-md-4 d-flex align-items-end">
          <button className="btn btn-primary btn-lg w-100" disabled={loading}>
            {loading
              ? <><span className="spinner-border spinner-border-sm me-2"></span>Checking...</>
              : <><i className="bi bi-search me-2"></i>Check Number</>}
          </button>
        </div>
      </form>

      {result && (
        <div className={`result-card ${result.blocked ? "result-blocked" : "result-clear"}`}>
          <div className="d-flex align-items-center gap-3 mb-3">
            <div className={`result-icon ${result.blocked ? "icon-blocked" : "icon-clear"}`}>
              <i className={`bi ${result.blocked ? "bi-shield-x" : "bi-shield-check"}`}></i>
            </div>
            <div>
              <h5 className="mb-0 fw-bold">
                {result.blocked ? "Number is Blocked" : "Number is Clear"}
              </h5>
              <span className="text-muted small">{result.phone_number}</span>
            </div>
            <span className={`badge ms-auto fs-6 ${result.blocked ? "bg-danger" : "bg-success"}`}>
              {result.blocked ? "BLOCKED" : "NOT BLOCKED"}
            </span>
          </div>

          {result.reason && (
            <div className="info-row">
              <span className="info-label"><i className="bi bi-chat-left-text me-1"></i>Reason</span>
              <span>{result.reason}</span>
            </div>
          )}
          {result.blocked_till && (
            <div className="info-row">
              <span className="info-label"><i className="bi bi-clock me-1"></i>Blocked Till</span>
              <span>{formatDate(result.blocked_till)}</span>
            </div>
          )}
        </div>
      )}
    </div> 
  );
}

// ─────────────────────────────────────────────────────────────────────────
// TAB 2 — Block Number
// ─────────────────────────────────────────────────────────────────────────
const INITIAL_FORM = {
  phone_number: "",
  block_type:   "temp",
  reason:       "",
  notes:        "",
  blocked_till: "",
  blocked_by:   "",
};

function BlockNumber() {
  const [form,    setForm]    = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [alert,   setAlert]   = useState(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.phone_number.trim()) { setAlert({ type: "warning", message: "Phone number is required." }); return; }
    if (!form.reason.trim())       { setAlert({ type: "warning", message: "Reason is required." });       return; }
    if (form.block_type === "temp" && !form.blocked_till) {
      setAlert({ type: "warning", message: "Blocked till date is required for temporary block." });
      return;
    }

    setLoading(true);
    setAlert(null);

    const payload = {
      phone_number: form.phone_number.trim(),
      block_type:   form.block_type,
      reason:       form.reason.trim(),
      notes:        form.notes.trim() || undefined,
      blocked_till: form.blocked_till || undefined,
      blocked_by:   form.blocked_by.trim() || undefined,
    };

    try {
      const res  = await fetch(`${API_BASE}/blocked`, {
        method:  "POST",
        headers,
        body:    JSON.stringify(payload),
      });
      const data = await res.json();

      if (!data.success) throw new Error(data.message || "API error");

      setAlert({ type: "success", message: `✅ ${form.phone_number} blocked successfully.` });
      setForm(INITIAL_FORM);
    } catch (err) {
      setAlert({ type: "danger", message: err.message });
    } finally {
      setLoading(false);
    }
  }

  return (
 <div> 
          <Alert {...(alert || {})} onClose={() => setAlert(null)} />

      <form onSubmit={handleSubmit}>
        <div className="row g-3">

          {/* Phone Number */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Phone Number <span className="text-danger">*</span>
            </label>
            <div className="input-group">
              <span className="input-group-text bg-light">
                <i className="bi bi-telephone-fill text-primary"></i>
              </span>
              <input
                type="text"
                name="phone_number"
                className="form-control"
                placeholder="03001234567"
                value={form.phone_number}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Block Type */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">Block Type <span className="text-danger">*</span></label>
            <div className="d-flex gap-3 mt-1">
              {["temp", "permanent"].map(t => (
                <div className="form-check" key={t}>
                  <input
                    className="form-check-input"
                    type="radio"
                    name="block_type"
                    id={`bt_${t}`}
                    value={t}
                    checked={form.block_type === t}
                    onChange={handleChange}
                  />
                  <label className="form-check-label text-capitalize" htmlFor={`bt_${t}`}>
                    {t === "temp"
                      ? <><i className="bi bi-clock me-1 text-warning"></i>Temporary</>
                      : <><i className="bi bi-shield-x me-1 text-danger"></i>Permanent</>}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Reason */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">
              Reason <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="reason"
              className="form-control"
              placeholder="e.g. Fraud suspected"
              value={form.reason}
              onChange={handleChange}
            />
          </div>

          {/* Blocked By */}
          <div className="col-md-6">
            <label className="form-label fw-semibold">Blocked By</label>
            <input
              type="text"
              name="blocked_by"
              className="form-control"
              placeholder="e.g. admin"
              value={form.blocked_by}
              onChange={handleChange}
            />
          </div>

          {/* Blocked Till — only for temp */}
          {form.block_type === "temp" && (
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Blocked Till <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                name="blocked_till"
                className="form-control"
                value={form.blocked_till}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
          )}

          {/* Notes */}
          <div className="col-12">
            <label className="form-label fw-semibold">Notes</label>
            <textarea
              name="notes"
              className="form-control"
              rows={3}
              placeholder="Additional notes..."
              value={form.notes}
              onChange={handleChange}
            />
          </div>

          {/* Submit */}
          <div className="col-12 d-flex gap-2">
            <button type="submit" className="btn btn-danger px-4" disabled={loading}>
              {loading
                ? <><span className="spinner-border spinner-border-sm me-2"></span>Blocking...</>
                : <><i className="bi bi-shield-x me-2"></i>Block Number</>}
            </button>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setForm(INITIAL_FORM)}>
              <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
            </button>
          </div>
        </div>
      </form>
    </div>  
  );
}

// ─────────────────────────────────────────────────────────────────────────
// MAIN PAGE 
// ─────────────────────────────────────────────────────────────────────────
export default function BlockedNumbers() {
  const [activeTab, setActiveTab] = useState("check");

  return (
    <MainLayout> 
      <div className="blocked-numbers-page">

        {/* Page Header */}
        <div className="page-header mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="header-icon">
              <i className="bi bi-shield-exclamation"></i>
            </div>
            <div>
              <h4 className="mb-0 fw-bold">Blocked Numbers</h4>
              <p className="text-muted mb-0 small">Check or block phone numbers from the system</p>
            </div>
          </div>
        </div>

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Tabs */}
          <div className="card-header bg-white border-bottom px-4 pt-3 pb-0">
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "check" ? "active" : ""}`}
                  onClick={() => setActiveTab("check")}
                >
                  <i className="bi bi-search me-2"></i>Check Number
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-semibold ${activeTab === "block" ? "active text-danger" : ""}`}
                  onClick={() => setActiveTab("block")}
                >
                  <i className="bi bi-shield-x me-2"></i>Block Number
                </button>
              </li>
            </ul>
          </div>

          {/* Tab Content */}
          <div className="card-body p-4">
            {activeTab === "check" ? <CheckNumber /> : <BlockNumber />}
          </div>
        </div>

      </div>
    </MainLayout>  
  );
}